import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { Oficina } from 'src/app/domain/model/catalogo/oficina';
import { TipoAsesorReal } from 'src/app/domain/model/catalogo/tipo-asesor-real';
import {
  META_MASIVA_ANIO_MAX_LENGTH,
  META_MASIVA_META_MES_MAX_LENGTH,
  MESES_META,
  PLACEHOLDER_SELECCIONE
} from 'src/app/common/constants-ejb';
import {
  onlyNumbersKeypress,
  sanitizeNumbersOnly,
  sanitizeText
} from 'src/app/common/input-utils';
import { MetaMasivaFormulario } from '../../services/metas.facade';

/**
 * Modal "Nueva Meta Masiva" (dumb component, HU1290096).
 *
 * <p>Legacy: NuevaMetaMasivaViewImpl. Campos en el orden del legacy: Mes * (con TODOS = 0), Año *,
 * Oficina * (casillas, multi-selección), Tipo Asesor * y Meta Mes *. La validación y el guardado viven en
 * el facade: el componente solo recoge los valores y emite {@link grabar}. Limpiar deja Mes y Tipo
 * Asesor sin selección, vacía Año y Meta Mes y borra los mensajes (no desmarca las oficinas, como el
 * `reset()` del legacy); Regresar cierra sin grabar.</p>
 *
 * <p>El container lo instancia solo mientras está visible, así que cada apertura parte de cero.</p>
 */
@Component({
  selector: 'app-meta-masiva-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    CheckboxModule
  ],
  template: `
    <p-dialog
      header="Nueva Meta Masiva"
      [visible]="true"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [closable]="true"
      [style]="{ width: '92vw', maxWidth: '36rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '640px': '95vw' }"
      (visibleChange)="!$event && regresar.emit()"
    >
      @if (mensajes().length > 0) {
        <div
          class="p-3 mb-3 rounded border bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700"
          role="alert"
        >
          @for (texto of mensajes(); track $index) {
            <div>{{ texto }}</div>
          }
        </div>
      }

      <div class="grid grid-cols-1 gap-y-3">
        <div>
          <label
            for="meta-mes"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Mes *</label
          >
          <p-dropdown
            inputId="meta-mes"
            [options]="meses"
            optionLabel="descripcion"
            optionValue="codigo"
            [placeholder]="placeholder"
            appendTo="body"
            [style]="{ width: '100%' }"
            [(ngModel)]="mes"
          ></p-dropdown>
        </div>

        <div>
          <label
            for="meta-anio"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Año *</label
          >
          <input
            id="meta-anio"
            pInputText
            class="w-full"
            aria-required="true"
            inputmode="numeric"
            autocomplete="off"
            [maxlength]="anioMaxLength"
            [(ngModel)]="anio"
            (keypress)="onlyNumbersKeypress($event)"
            (input)="anio = sanitizeNumbersOnly(anio)"
          />
        </div>

        <fieldset class="m-0 p-0 border-0 min-w-0">
          <legend
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 p-0"
          >
            Oficina *
          </legend>
          <div
            class="max-h-48 overflow-y-auto rounded border border-gray-200 dark:border-gray-600 p-2"
          >
            @for (oficina of oficinas(); track oficina.nmconsecutivo) {
              <div class="flex items-center min-h-[44px]">
                <p-checkbox
                  [(ngModel)]="oficinasSeleccionadas"
                  [value]="oficina.nmconsecutivo"
                  [inputId]="'meta-oficina-' + oficina.nmconsecutivo"
                ></p-checkbox>
                <label
                  [for]="'meta-oficina-' + oficina.nmconsecutivo"
                  class="ml-2 text-sm cursor-pointer text-gray-800 dark:text-gray-200"
                  >{{ oficina.dsoficina }}</label
                >
              </div>
            }
          </div>
        </fieldset>

        <div>
          <label
            for="meta-tipo-asesor"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Tipo Asesor *</label
          >
          <p-dropdown
            inputId="meta-tipo-asesor"
            [options]="tiposAsesor()"
            optionLabel="dstipo"
            optionValue="cdtipoReal"
            [placeholder]="placeholder"
            appendTo="body"
            [style]="{ width: '100%' }"
            [(ngModel)]="tipoAsesor"
          ></p-dropdown>
        </div>

        <div>
          <label
            for="meta-meta-mes"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Meta Mes *</label
          >
          <input
            id="meta-meta-mes"
            pInputText
            class="w-full"
            aria-required="true"
            inputmode="decimal"
            autocomplete="off"
            [maxlength]="metaMaxLength"
            [(ngModel)]="metaMes"
            (input)="metaMes = sanitizeText(metaMes)"
            (keyup.enter)="grabarClick()"
          />
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Regresar"
            icon="pi pi-arrow-left"
            severity="secondary"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="regresar.emit()"
          ></p-button>
          <p-button
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="limpiarClick()"
          ></p-button>
          <p-button
            label="Grabar"
            icon="pi pi-check"
            styleClass="min-h-[44px]"
            [loading]="guardando()"
            (onClick)="grabarClick()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class MetaMasivaDialogComponent {
  readonly oficinas = input<Oficina[]>([]);
  readonly tiposAsesor = input<TipoAsesorReal[]>([]);
  readonly mensajes = input<string[]>([]);
  readonly guardando = input(false);

  readonly grabar = output<MetaMasivaFormulario>();
  readonly regresar = output<void>();
  readonly limpiar = output<void>();

  /** Copia mutable: el `p-dropdown` tipa `options` como `any[]` y no acepta un arreglo `readonly`. */
  protected readonly meses = [...MESES_META];
  protected readonly placeholder = PLACEHOLDER_SELECCIONE;
  protected readonly anioMaxLength = META_MASIVA_ANIO_MAX_LENGTH;
  protected readonly metaMaxLength = META_MASIVA_META_MES_MAX_LENGTH;
  protected readonly sanitizeText = sanitizeText;
  protected readonly sanitizeNumbersOnly = sanitizeNumbersOnly;
  protected readonly onlyNumbersKeypress = onlyNumbersKeypress;

  mes: number | null = null;
  anio = '';
  oficinasSeleccionadas: number[] = [];
  tipoAsesor: string | null = null;
  metaMes = '';

  grabarClick(): void {
    // Evita el doble envío (Enter o clic repetido) mientras se guarda.
    if (this.guardando()) return;
    this.grabar.emit({
      mes: this.mes,
      anio: this.anio,
      oficinas: [...this.oficinasSeleccionadas],
      tipoAsesor: this.tipoAsesor,
      metaMes: this.metaMes
    });
  }

  /** Legacy: reset() — no toca las casillas de oficina. */
  limpiarClick(): void {
    this.mes = null;
    this.tipoAsesor = null;
    this.anio = '';
    this.metaMes = '';
    this.limpiar.emit();
  }
}
